import { Observable, BehaviorSubject, Subject } from "rxjs";
import { tap, finalize } from "rxjs/operators";
import { CollectionViewer, DataSource } from "@angular/cdk/collections";
import { TemplateListService } from "./service/templateList.service";
import { TemplateService } from "./service/template.service";
import { TemplateList, TemplateListReq } from "./model/TemplateList";
import { MatSort } from "@angular/material/sort";

export class TemplateListDataSource implements DataSource<TemplateList> {
  private templateSubject = new BehaviorSubject<TemplateList[]>([]);
  private resultNumber = new BehaviorSubject<number>(0);
  // Fires whenever `temData` has been refreshed from the backend, so callers can react to
  // updated fields (e.g. re-check whether any row's RegHub monitor is still 'outdated').
  public dataRefreshed = new Subject<void>();
  public loading = false;
  public temData: TemplateList[] = [];
  public sort: MatSort;
  // Every load (from cache, backend or background poll) takes the next sequence number, and a
  // backend response is only applied if no newer load has started since. Otherwise responses land
  // in whatever order they arrive: e.g. switching My/Shared while an earlier request (or a
  // background poll sent with the old choice) is still in flight let the stale response hide the
  // spinner and show - and cache - the old list until the right one arrived.
  private loadSeq = 0;
  // A spinner-backed backend load is in flight; background polls skip until it lands.
  private backendInFlight = false;

  constructor(
    private service: TemplateListService,
    private templateService: TemplateService
  ) {}

  loadDatas(
    id: number,
    myTemplate: boolean,
    inProgress: number,
    pageIndex: number, // 1- current; 2- archived
    pageSize: number,
    sortDirection: string,
    sortColumn: string
  ) {
    var cache = this.templateService.loadFromCache();
    if (cache != null && cache.length > 0 && inProgress == 1) {
      this.loadSeq++; // supersede any backend response still in flight
      this.backendInFlight = false;
      this.processData(cache, pageIndex, pageSize, sortDirection, sortColumn);
    } else {
      this.callBackend(
        id,
        myTemplate,
        inProgress,
        pageIndex,
        pageSize,
        sortDirection,
        sortColumn
      );
    }
  }

  // load data from backend.
  reloadDatas(
    id: number,
    myTemplate: boolean,
    inProgress: number,
    pageIndex: number, // 1- current; 2- archived
    pageSize: number,
    sortDirection: string,
    sortColumn: string
  ) {
    this.templateService.removeCache();
    this.callBackend(
      id,
      myTemplate,
      inProgress,
      pageIndex,
      pageSize,
      sortDirection,
      sortColumn
    );
  }

  // Like callBackend, but skips the loading-animation toggle so it can run in the background
  // (e.g. polling for RegHub monitor status) without flickering the spinner or disabling buttons.
  refreshSilently(
    id: number,
    myTemplate: boolean,
    inProgress: number,
    pageIndex: number,
    pageSize: number,
    sortDirection: string,
    sortColumn: string
  ) {
    if (this.backendInFlight) return; // the pending load will bring fresh data anyway.

    let req: TemplateListReq = {
      userid: id,
      myTemplate: myTemplate,
      inProgress: inProgress,
    };
    const seq = ++this.loadSeq;

    this.service.getTemplateList(req).subscribe(
      (data) => {
        if (seq !== this.loadSeq) return; // a newer load superseded this poll.
        if (inProgress == 1) {
          this.templateService.cacheTemplateList(data);
        }
        this.processData(data, pageIndex, pageSize, sortDirection, sortColumn);
      },
      (error) => {
        console.log(error);
      }
    );
  }

  hasOutdatedMonitor(): boolean {
    return this.temData.some((t) =>
      t.monitoring?.toLowerCase().includes("outdated")
    );
  }

  hasUpgradingTemplate(): boolean {
    return this.temData.some((t) => t.status === "upgrading");
  }

  // Looks across the full loaded dataset (not just the current page), so callers can check a
  // csskey's monitoring status even when it isn't on the visible page.
  getMonitorFromAll(csskey: number): string | null {
    const item = this.temData.find((t) => t.csskey == csskey);
    return item ? item.monitoring : null;
  }

  callBackend(
    id: number,
    myTemplate: boolean,
    inProgress: number,
    pageIndex: number,
    pageSize: number,
    sortDirection: string,
    sortColumn: string
  ) {
    let req: TemplateListReq = {
      userid: id,
      myTemplate: myTemplate,
      inProgress: inProgress, //1- current; 2- archived
    };

    const seq = ++this.loadSeq;
    this.backendInFlight = true;
    this.templateService.showLoadingAnimation(true);
    this.service.getTemplateList(req).subscribe(
      (data) => {
        // Stale: a newer load started since (it owns the spinner and the list now).
        if (seq !== this.loadSeq) return;
        this.backendInFlight = false;
        if (inProgress == 1) {
          // cache the current template list
          this.templateService.cacheTemplateList(data);
        }
        this.processData(data, pageIndex, pageSize, sortDirection, sortColumn);
      },
      (error) => {
        console.log(error);
        if (seq !== this.loadSeq) return;
        this.backendInFlight = false;
        if (error.status == 404) {
          this.templateSubject.next(null);
          this.resultNumber.next(0);
        }

        this.templateService.showLoadingAnimation(false);
      }
    );
  }

  processData(
    data: TemplateList[],
    pageIndex: number,
    pageSize: number,
    direction: string,
    column: string
  ) {
    //console.log("load " + data.length);
    if (direction == "desc" && column == "cssDate") {
      this.temData = data.sort((a, b) =>
        a.cssDate < b.cssDate ? 1 : b.cssDate < a.cssDate ? -1 : 0
      );
    } else if (direction == "asc" && column == "cssDate") {
      this.temData = data.sort((a, b) =>
        a.cssDate > b.cssDate ? 1 : b.cssDate > a.cssDate ? -1 : 0
      );
    } else if (direction == "desc" && column == "auditName") {
      this.temData = data.sort((a, b) =>
        a.auditName.toLowerCase() < b.auditName.toLocaleLowerCase()
          ? 1
          : b.auditName.toLocaleLowerCase() < a.auditName.toLocaleLowerCase()
          ? -1
          : 0
      );
    } else {
      this.temData = data.sort((a, b) =>
        a.auditName.toLowerCase() > b.auditName.toLocaleLowerCase()
          ? 1
          : b.auditName.toLocaleLowerCase() > a.auditName.toLocaleLowerCase()
          ? -1
          : 0
      );
    }

    var pageData: TemplateList[] = [];
    let start = pageIndex * pageSize;

    for (var i = 0; i < this.temData.length; i++) {
      if (i >= start && i < start + pageSize) {
        if (!this.temData[i].isShared) {
          this.temData[i].permission = "N/A";
        } else {
          this.temData[i].permission = this.temData[i].permission.trim();
        }

        pageData.push(this.temData[i]);
      }
    }
    this.templateSubject.next(pageData);
    this.resultNumber.next(this.temData.length);
    this.templateService.showLoadingAnimation(false);
    this.dataRefreshed.next();
  }

  loadPage(pageIndex: number, pageSize: number) {
    let start = pageIndex * pageSize;
    var pageData: TemplateList[] = [];

    for (var i = 0; i < this.temData.length; i++) {
      if (i >= start && i < start + pageSize) {
        pageData.push(this.temData[i]);
      }
    }
    this.templateSubject.next(pageData);
  }

  searchTemplate(keystring: string): number {
    var pageData: TemplateList[] = [];
    for (var i = 0; i < this.temData.length; i++) {
      let index = this.temData[i].auditName
        .toUpperCase()
        .indexOf(keystring.toUpperCase());
      if (index != -1) {
        pageData.push(this.temData[i]);
        continue;
      }
    }
    if (pageData.length > 0) {
      this.resultNumber.next(pageData.length);
      this.templateSubject.next(pageData);
      return 0;
    } else {
      this.resultNumber.next(0);
      this.templateSubject.next(null);
      return -1;
    }
  }

  getItem(csskey: number): TemplateList {
    var data = this.templateSubject.value;
    return data.find((x) => x.csskey == csskey);
  }

  getPercentage(csskey: number) {
    let item = this.getTemplateItem(csskey);
    if (item) {
      return item.percentComplete;
    } else {
      return null;
    }
  }

  getMonitor(csskey: number) {
    let item = this.getTemplateItem(csskey);
    if (item) {
      return item.monitoring;
    } else {
      return null;
    }
  }

  getDualLanguage(csskey: number) {
    let item = this.getTemplateItem(csskey);
    if (item) {
      return item.dualLanguage;
    } else {
      return 0;
    }
  }

  getFacilityLocation(csskey: number) {
    let item = this.getTemplateItem(csskey);
    if (item) {
      return item.facilityLocation;
    } else {
      return null;
    }
  }

  getAuditDate(csskey: number) {
    let item = this.getTemplateItem(csskey);
    if (item) {
      return (
        new Date(item.startDate).toDateString() +
        " - " +
        new Date(item.endDate).toDateString()
      );
    } else {
      return null;
    }
  }

  getTemplateItem(csskey: number): TemplateList {
    var data = this.templateSubject.value;

    for (var i = 0; i < data.length; i++) {
      if (data[i].csskey == csskey) {
        return data[i];
      }
    }
    return {} as TemplateList;
  }

  getNumber(): Observable<number> {
    return this.resultNumber.asObservable();
  }

  deleteRow(rowid: number): Observable<TemplateList[]> {
    var temData = this.templateSubject.value;
    temData.splice(rowid, 1);
    this.templateSubject.next(temData);
    this.resultNumber.next(this.resultNumber.value - 1);
    return this.templateSubject.asObservable();
  }

  connect(collectionViewer: CollectionViewer): Observable<TemplateList[]> {
    return this.templateSubject.asObservable();
  }

  disconnect(collectionViewer: CollectionViewer): void {
    this.templateSubject.complete();
  }
}
